import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { MODULES } from "../lib/modules";
import { useEntityList } from "../hooks/useEntityList";
import { useAuth } from "../state/AuthContext";
import { IconCheck } from "../components/Icons";
import { PageHeader } from "../components/RequestRouting";

export function ModulePage({ moduleId }: { moduleId: string }) {
  const mod = MODULES.find((m) => m.id === moduleId) ?? MODULES[0];
  const { moduleIds, homePath } = useAuth();
  const { rows, loading, waking, error, source, mutateStatus } = useEntityList(mod.entity);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!moduleIds.includes(mod.id)) {
    return <Navigate to={homePath} replace />;
  }

  const selected = useMemo(
    () => rows.find((r) => String(r.id) === selectedId) ?? rows[0] ?? null,
    [rows, selectedId],
  );

  const columns = useMemo(() => {
    if (!rows[0]) return ["id"];
    return Object.keys(rows[0]).filter((k) => k !== "id");
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader title={mod.label} subtitle={mod.description} />
      </div>

      {waking && (
        <div className="px-4 py-3 bg-teal-soft border border-teal/20 text-navy text-sm">
          Starting up — this may take a few seconds.
        </div>
      )}

      {error && source === "local" && waking === false && null}

      <div className="grid lg:grid-cols-[1.4fr_0.9fr] gap-4">
        <div className="bg-white border border-black/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
            <h2 className="font-semibold text-navy">Records</h2>
            <span className="text-xs text-slate">
              {loading ? "Loading…" : `${rows.length} rows`}
            </span>
          </div>
          <div className="overflow-auto max-h-[560px]">
            <table className="w-full text-sm">
              <thead className="bg-mist sticky top-0">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c}
                      className="text-left font-medium text-slate px-3 py-2 capitalize"
                    >
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={String(row.id)}
                    className={`border-t border-black/5 cursor-pointer hover:bg-teal-soft/40 ${
                      String(selected?.id) === String(row.id) ? "bg-teal-soft/60" : ""
                    }`}
                    onClick={() => setSelectedId(String(row.id))}
                  >
                    {columns.map((c) => (
                      <td key={c} className="px-3 py-2 text-navy">
                        {String(row[c] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-black/5 p-4 space-y-3">
          <h2 className="font-semibold text-navy">Detail</h2>
          {!selected ? (
            <p className="text-sm text-slate">Select a row.</p>
          ) : (
            <>
              {columns.map((c) => (
                <div key={c}>
                  <div className="text-xs uppercase tracking-wider text-slate">
                    {c.replace(/_/g, " ")}
                  </div>
                  <div className="text-sm text-navy">{String(selected[c] ?? "")}</div>
                </div>
              ))}
              {"status" in selected && (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-navy text-white"
                  onClick={() => void mutateStatus(String(selected.id), "Reviewed")}
                >
                  <IconCheck size={16} /> Mark reviewed
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
